/**
 * Audit run console — status, progress, tracks, and next actions.
 */

import { KeyboardEvent, useEffect, useMemo, useState, type CSSProperties } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  api,
  AuditDetailsResponse,
  AuditProgressResponse,
  AuditTaskProgress,
  TaskResponse,
} from '../lib/api'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { StatusBadge } from '../components/StatusBadge'
import { Badge } from '../components/design-system/Badge'
import { Button } from '../components/design-system/Button'
import { useLocale } from '../contexts/LocaleContext'
import { statusLabel, trackLabel } from '../lib/i18n'

const IN_FLIGHT = new Set(['pending', 'queued', 'running'])

type TaskRow = {
  id: string
  track: string
  status: string
  findings_count: number
  task_type?: string
  error_message?: string | null
}

function packChip(item: Record<string, unknown>): string {
  const id = String(item.pack_id || item.id || '').trim()
  const version = item.version ? `@${item.version}` : ''
  return id ? `${id}${version}` : JSON.stringify(item)
}

function taskRows(audit: AuditDetailsResponse, progress: AuditProgressResponse | null): TaskRow[] {
  const fromProgress = progress?.tasks || []
  if (fromProgress.length > 0) {
    return fromProgress.map((task: AuditTaskProgress) => ({
      id: task.task_id,
      track: task.track,
      status: task.status,
      findings_count: task.findings_count,
      task_type: task.task_type,
      error_message: task.error_message,
    }))
  }
  if (audit.tasks.length > 0) {
    return audit.tasks.map((task: TaskResponse) => ({
      id: task.id,
      track: task.track,
      status: task.status,
      findings_count: task.findings_count,
      error_message: task.error_message,
    }))
  }
  return audit.requested_tracks.map((track, index) => ({
    id: `planned-${track}-${index}`,
    track,
    status: IN_FLIGHT.has(audit.status) ? 'queued' : 'pending',
    findings_count: 0,
  }))
}

const mono: CSSProperties = {
  fontFamily: 'var(--reviewer-font-mono)',
}

const cardStyle: CSSProperties = {
  border: '1px solid var(--reviewer-border)',
  background: 'var(--reviewer-surface)',
  padding: '20px',
}

function KpiCard({
  label,
  value,
  tone,
  hrefLabel,
  onOpen,
  ariaLabel,
}: {
  label: string
  value: number
  tone?: string
  hrefLabel?: string
  onOpen: () => void
  ariaLabel: string
}) {
  function onKey(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onOpen()
    }
  }
  return (
    <div
      className="card"
      role="link"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={onKey}
      aria-label={ariaLabel}
      style={{
        ...cardStyle,
        cursor: 'pointer',
        borderLeft: `3px solid ${tone || 'var(--reviewer-border-strong)'}`,
      }}
    >
      <div style={{ ...mono, fontSize: 11, color: 'var(--reviewer-text-tertiary)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
        {label}
      </div>
      <div style={{ ...mono, fontSize: 32, fontWeight: 600, color: tone || 'var(--reviewer-text)', marginTop: 6 }}>
        {value}
      </div>
      {hrefLabel ? (
        <div style={{ ...mono, fontSize: 12, color: 'var(--reviewer-accent)', marginTop: 8 }}>{hrefLabel}</div>
      ) : null}
    </div>
  )
}

export function AuditDetailsPage() {
  const { t, locale } = useLocale()
  const { auditId } = useParams<{ auditId: string }>()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [audit, setAudit] = useState<AuditDetailsResponse | null>(null)
  const [progress, setProgress] = useState<AuditProgressResponse | null>(null)

  async function loadAudit(showSpinner = true) {
    if (!auditId) return
    try {
      if (showSpinner) setLoading(true)
      setError(null)
      const [data, prog] = await Promise.all([
        api.getAuditDetails(auditId),
        api.getAuditProgress(auditId).catch(() => null),
      ])
      setAudit(data)
      if (prog) setProgress(prog)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('audit.loading'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (auditId) void loadAudit()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auditId])

  useEffect(() => {
    if (!audit || !IN_FLIGHT.has(audit.status)) return
    let cancelled = false
    async function tick() {
      try {
        const next = await api.getAuditProgress(audit!.id)
        if (cancelled) return
        setProgress(next)
        if (!IN_FLIGHT.has(next.status)) {
          await loadAudit(false)
        }
      } catch {
        /* keep last known status */
      }
    }
    const timer = window.setInterval(tick, 3000)
    void tick()
    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [audit?.id, audit?.status])

  const rows = useMemo(() => (audit ? taskRows(audit, progress) : []), [audit, progress])
  const dateLocale = locale === 'ru' ? 'ru-RU' : 'en-US'

  if (loading) {
    return <LoadingSpinner message={t('audit.loading')} />
  }

  if (error) {
    return (
      <div className="card card-error">
        <p className="text-sm">{error}</p>
        <button onClick={() => navigate('/audits')} className="btn btn-sm btn-primary mt-4">
          {t('audits.back')}
        </button>
      </div>
    )
  }

  if (!audit) return null

  const totalTasks = progress?.total_tasks || rows.length
  const completed = progress?.completed_tasks ?? rows.filter((row) => ['success', 'completed'].includes(row.status)).length
  const running = progress?.running_tasks ?? rows.filter((row) => row.status === 'running').length
  const failed = progress?.failed_tasks ?? rows.filter((row) => row.status === 'failed').length
  const pending = Math.max(0, totalTasks - completed - running - failed)
  const percent = progress?.progress_percent ?? (totalTasks ? Math.round(((completed + failed) / totalTasks) * 10000) / 100 : 0)
  const live = IN_FLIGHT.has(audit.status)

  const doneW = totalTasks ? (completed / totalTasks) * 100 : 0
  const runW = totalTasks ? (running / totalTasks) * 100 : 0
  const failW = totalTasks ? (failed / totalTasks) * 100 : 0
  const waitW = Math.max(0, 100 - doneW - runW - failW)

  return (
    <div className="space-y-5" data-testid="audit-detail" style={{ ...mono }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
        <Button size="sm" variant="secondary" onClick={() => navigate('/audits')}>
          ← {t('common.back')}
        </Button>
        <div style={{ flex: 1, minWidth: 280 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <h1 style={{ margin: 0, fontSize: 26, fontWeight: 600 }}>{audit.project_name}</h1>
            <StatusBadge status={audit.status} />
            {live ? (
              <Badge variant="info" size="sm">{t('audit.live')}</Badge>
            ) : null}
          </div>
          <p style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--reviewer-text-secondary)' }}>
            {t('audit.meta')} · {new Date(audit.created_at).toLocaleString(dateLocale)}
            {audit.id ? ` · ${audit.id.slice(0, 8)}` : ''}
          </p>
        </div>
      </div>

      <div style={cardStyle} data-testid="audit-progress">
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
          <div style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--reviewer-text-tertiary)' }}>
            {t('audit.progress')}
          </div>
          <div style={{ fontSize: 13, color: 'var(--reviewer-text-secondary)' }}>
            <strong style={{ color: 'var(--reviewer-text)', fontSize: 18 }}>{percent.toFixed(percent % 1 ? 1 : 0)}%</strong>
            {' · '}
            {t('audit.tasksOf', { done: completed, total: totalTasks })}
          </div>
        </div>
        <div
          className="audit-progress-track"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(percent)}
          aria-label={t('audit.progress')}
        >
          {doneW > 0 ? <div className="audit-progress-seg audit-progress-seg-done" style={{ width: `${doneW}%` }} /> : null}
          {runW > 0 ? <div className="audit-progress-seg audit-progress-seg-run" style={{ width: `${runW}%` }} /> : null}
          {failW > 0 ? <div className="audit-progress-seg audit-progress-seg-fail" style={{ width: `${failW}%` }} /> : null}
          {waitW > 0 ? <div className="audit-progress-seg audit-progress-seg-wait" style={{ width: `${waitW}%` }} /> : null}
        </div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 10, fontSize: 12, color: 'var(--reviewer-text-secondary)' }}>
          {running > 0 ? <span>{t('audit.runningCount', { n: running })}</span> : null}
          {failed > 0 ? <span style={{ color: 'var(--reviewer-danger)' }}>{t('audit.failedCount', { n: failed })}</span> : null}
          {pending > 0 ? <span>{t('audit.pendingCount', { n: pending })}</span> : null}
          {progress?.estimated_completion_at ? (
            <span>{t('audit.eta', { time: new Date(progress.estimated_completion_at).toLocaleTimeString(dateLocale) })}</span>
          ) : null}
        </div>
      </div>

      {audit.error_message ? (
        <div className="card card-error">
          <p className="text-sm">{audit.error_message}</p>
        </div>
      ) : null}

      <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
        <KpiCard
          label={t('audit.totalFindings')}
          value={progress?.total_findings ?? audit.total_findings}
          hrefLabel={t('audit.viewFindings')}
          onOpen={() => navigate(`/findings?audit_run_id=${audit.id}`)}
          ariaLabel={t('audit.viewAllAria')}
        />
        <KpiCard
          label={t('audit.critical')}
          value={audit.critical_count}
          tone="var(--reviewer-danger)"
          hrefLabel={audit.critical_count > 0 ? t('audit.viewCritical') : undefined}
          onOpen={() => navigate(`/findings?audit_run_id=${audit.id}&severity=critical`)}
          ariaLabel={t('audit.viewCriticalAria')}
        />
        <KpiCard
          label={t('audit.high')}
          value={audit.high_count}
          tone="var(--reviewer-warning)"
          hrefLabel={audit.high_count > 0 ? t('audit.viewHigh') : undefined}
          onOpen={() => navigate(`/findings?audit_run_id=${audit.id}&severity=high`)}
          ariaLabel={t('audit.viewHighAria')}
        />
        <KpiCard
          label={t('audit.mediumLowLabel')}
          value={audit.medium_count + audit.low_count}
          tone="var(--reviewer-info)"
          hrefLabel={audit.medium_count + audit.low_count > 0 ? t('audit.viewMediumLow') : undefined}
          onOpen={() => navigate(`/findings?audit_run_id=${audit.id}&severity=medium,low`)}
          ariaLabel={t('audit.viewMediumLowAria')}
        />
      </div>

      {(audit.risk_score !== null || audit.debt_score !== null || audit.compliance_score !== null) && (
        <div className="card" data-testid="score-summary" style={cardStyle}>
          <h2 style={{ margin: '0 0 16px', fontSize: 16 }}>{t('audit.scoresTitle')}</h2>
          <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', marginBottom: 16 }}>
            {audit.risk_score != null && (
              <div>
                <div style={{ fontSize: 11, color: 'var(--reviewer-text-tertiary)' }}>{t('audit.riskScore')}</div>
                <div style={{ fontSize: 28, color: 'var(--reviewer-danger)' }}>{audit.risk_score.toFixed(1)}</div>
              </div>
            )}
            {audit.debt_score != null && (
              <div>
                <div style={{ fontSize: 11, color: 'var(--reviewer-text-tertiary)' }}>{t('audit.debtScore')}</div>
                <div style={{ fontSize: 28, color: 'var(--reviewer-warning)' }}>{audit.debt_score.toFixed(1)}</div>
              </div>
            )}
            {audit.compliance_score != null && (
              <div>
                <div style={{ fontSize: 11, color: 'var(--reviewer-text-tertiary)' }}>{t('audit.complianceScore')}</div>
                <div style={{ fontSize: 28, color: 'var(--reviewer-info)' }}>{audit.compliance_score.toFixed(1)}</div>
              </div>
            )}
          </div>
          <a
            href={`/audit/${audit.id}/scores`}
            onClick={(e) => {
              e.preventDefault()
              navigate(`/audit/${audit.id}/scores`)
            }}
            className="btn btn-sm btn-secondary no-underline inline-block"
          >
            {t('audit.viewScoresDetail')}
          </a>
        </div>
      )}

      <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={cardStyle} data-testid="audit-tasks">
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
              <h2 style={{ margin: 0, fontSize: 16 }}>{t('audit.tasks')}</h2>
              <span style={{ fontSize: 12, color: 'var(--reviewer-text-tertiary)' }}>{rows.length}</span>
            </div>
            {rows.length === 0 ? (
              <p style={{ margin: 0, fontSize: 13, color: 'var(--reviewer-text-secondary)' }}>{t('audit.tasksEmptyBody')}</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {rows.map((task) => (
                  <div
                    key={task.id}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(0, 1fr) auto',
                      gap: 12,
                      alignItems: 'center',
                      padding: '10px 12px',
                      background: 'var(--reviewer-bg-secondary)',
                      border: '1px solid var(--reviewer-border)',
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 600 }}>{trackLabel(locale, task.track)}</div>
                      <div style={{ fontSize: 11, color: 'var(--reviewer-text-tertiary)' }}>
                        {task.task_type ? `${t('audit.taskType')} ${task.task_type} · ` : ''}
                        {t('audit.findingsCount', { n: task.findings_count })}
                        {task.error_message ? ` · ${task.error_message}` : ''}
                      </div>
                    </div>
                    <StatusBadge status={task.status} className="text-xs" />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={cardStyle}>
            <h2 style={{ margin: '0 0 14px', fontSize: 16 }}>{t('audit.timelineTitle')}</h2>
            {audit.workflow_events.length === 0 ? (
              <p style={{ margin: 0, fontSize: 13, color: 'var(--reviewer-text-secondary)' }}>{t('audit.timelineEmpty')}</p>
            ) : (
              <div>
                {audit.workflow_events.map((event, index) => (
                  <div key={event.id} style={{ display: 'flex', gap: 12 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <div style={{ width: 8, height: 8, background: 'var(--reviewer-accent)' }} />
                      {index < audit.workflow_events.length - 1 ? (
                        <div style={{ width: 1, flex: 1, background: 'var(--reviewer-border)', minHeight: 20 }} />
                      ) : null}
                    </div>
                    <div style={{ paddingBottom: 14 }}>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{statusLabel(locale, event.new_state)}</div>
                      <div style={{ fontSize: 12, color: 'var(--reviewer-text-secondary)' }}>
                        {new Date(event.created_at).toLocaleString(dateLocale)}
                      </div>
                      {event.transition_reason ? (
                        <div style={{ fontSize: 12, color: 'var(--reviewer-text-tertiary)', marginTop: 4 }}>
                          {event.transition_reason}
                        </div>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={cardStyle}>
            <h3 style={{ margin: '0 0 14px', fontSize: 14 }}>{t('audit.config')}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 13 }}>
              <div>
                <div style={{ fontSize: 11, color: 'var(--reviewer-text-tertiary)', marginBottom: 6 }}>{t('audit.requestedTracks')}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {audit.requested_tracks.length === 0 ? t('audit.none') : audit.requested_tracks.map((track) => (
                    <Badge key={track} variant="default" size="sm">{trackLabel(locale, track)}</Badge>
                  ))}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: 'var(--reviewer-text-tertiary)', marginBottom: 6 }}>{t('audit.executedTracks')}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {audit.tracks_executed.length === 0 ? (
                    <span style={{ color: 'var(--reviewer-text-tertiary)' }}>{t('audit.none')}</span>
                  ) : audit.tracks_executed.map((track) => (
                    <Badge key={track} variant="success" size="sm">{trackLabel(locale, track)}</Badge>
                  ))}
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8 }}>
                <span style={{ color: 'var(--reviewer-text-tertiary)' }}>{t('audit.quickScan')}</span>
                <span>{audit.quick_scan ? t('audit.yes') : t('audit.no')}</span>
                <span style={{ color: 'var(--reviewer-text-tertiary)' }}>{t('audit.aiAnalysis')}</span>
                <span>{audit.include_ai_analysis ? t('audit.enabled') : t('audit.disabled')}</span>
                <span style={{ color: 'var(--reviewer-text-tertiary)' }}>{t('audit.maxFindings')}</span>
                <span>{audit.max_findings_per_track}</span>
              </div>
              {audit.selected_pack_ids && audit.selected_pack_ids.length > 0 ? (
                <div>
                  <div style={{ fontSize: 11, color: 'var(--reviewer-text-tertiary)', marginBottom: 6 }}>{t('audit.packsUsed')}</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {audit.selected_pack_ids.map((pack, index) => (
                      <Badge key={`${packChip(pack)}-${index}`} variant="info" size="sm">{packChip(pack)}</Badge>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          <div style={cardStyle}>
            <h3 style={{ margin: '0 0 12px', fontSize: 14 }}>{t('audit.actions')}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Button fullWidth size="sm" variant="primary" onClick={() => navigate(`/findings?audit_run_id=${audit.id}`)}>
                {t('audit.actionFindings')}
              </Button>
              <Button fullWidth size="sm" variant="secondary" data-testid="audit-evidence-link" onClick={() => navigate(`/evidence?audit_run_id=${audit.id}`)}>
                {t('audit.actionEvidence')}
              </Button>
              <Button fullWidth size="sm" variant="secondary" onClick={() => navigate(`/audit/${audit.id}/scores`)}>
                {t('audit.actionScores')}
              </Button>
              <Button fullWidth size="sm" variant="secondary" onClick={() => navigate(`/audit/${audit.id}/reports`)}>
                {t('audit.actionReports')}
              </Button>
              <Button fullWidth size="sm" variant="secondary" onClick={() => navigate('/fix-with-ai')}>
                {t('audit.actionFix')}
              </Button>
              <Button fullWidth size="sm" variant="secondary" onClick={() => navigate(`/re-audit?audit_id=${audit.id}`)}>
                {t('audit.actionReaudit')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
